import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { Button } from 'components/Button'
import { Dialog, DialogContent } from 'components/Dialog'
import { Heading } from 'components/Heading'
import { Text } from 'components/Text'
import { useAnalytics } from 'hooks'
import { selectCurrentUserId } from 'modules/common'
import { useAppSelector } from 'modules/core'
import { safeGetLocalStorage, safeSetLocalStorage } from 'modules/core/services/safe-local-storage'
import { useEffect, useState } from 'react'
import { useReferralProgram } from './use-referral-program'

const SEEN_KEY_PREFIX = 'referral-referee-welcome-seen:'

const wasSeen = (userId: string): boolean => safeGetLocalStorage(SEEN_KEY_PREFIX + userId) === '1'

const markSeen = (userId: string): void => safeSetLocalStorage(SEEN_KEY_PREFIX + userId, '1')

/**
 * One-time celebration dialog for the invited friend (referee) — fires once on
 * first app load after their 25 credits have landed. No action required: the
 * credits are already in their account. Dismiss via the button or the close
 * chrome; the seen-flag is written to localStorage so it never re-fires.
 */
export const RefereeWelcomeCelebration = () => {
  const { info } = useReferralProgram()
  const currentUserId = useAppSelector(selectCurrentUserId)
  const { track } = useAnalytics()

  const credits = info?.receivedRefereeCredits ?? null
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!credits || !currentUserId) return
    if (wasSeen(currentUserId)) return
    setOpen(true)
    track(AnalyticsEvents.Referrals.REFERRAL_CELEBRATION_VIEWED, {
      role: 'referee',
      credits,
    })
  }, [credits, currentUserId, track])

  const handleClose = () => {
    if (currentUserId) markSeen(currentUserId)
    setOpen(false)
  }

  if (!open || !credits) return null

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        if (!next) handleClose()
      }}
    >
      <DialogContent variant="dark" size="sm" withCloseButton>
        <div className="flex flex-col items-center text-center">
          {/* Acid hero — single loud element, mirrors the referrer claim dialog. */}
          <div className="text-[44px] leading-none font-semibold tracking-[-0.02em] text-acid">
            +{credits}
            <span className="ml-1 text-[22px] font-medium tracking-normal text-acid">cr</span>
          </div>
          <Heading level="h2" className="mt-4">
            Your friend gave you {credits} free credits
          </Heading>
          <Text as="p" variant="body-sm" tone="paper-dim" className="mt-2 max-w-[36ch]">
            They're already in your account. Credits never expire. Use them on your first posts.
          </Text>
        </div>

        <div className="mt-6 flex justify-center">
          <Button type="button" variant="t3-primary" size="t3-md" onClick={handleClose}>
            Let's go
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
